import { CommonModule, CurrencyPipe } from "@angular/common";
import { Component, EventEmitter, inject, Input, Output } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { take } from "rxjs";
import { ConfirmActionModalComponent } from "@shared/modals/confirm-action-modal/confirm-action-modal.component";
import {
  canDeletePersistedBatch,
  canDeletePersistedTicket,
} from "../../helpers/ticket-sales-lock.helper";
import { CustomDateTimePipe } from "../../pipes/custom-date-time.pipe";
import { ButtonComponent } from "../button/button.component";
import { BatchTransitionDialogFacade } from "../batch-transition-modal/batch-transition-dialog.facade";
import {
  TicketBatch,
  TicketInBatch,
  TicketTransitionOption,
} from "../modal-tickets/types/ticket-batch.type";

@Component({
  selector: "app-ticket-table",
  providers: [CustomDateTimePipe, BatchTransitionDialogFacade],
  imports: [CommonModule, CurrencyPipe, ButtonComponent],
  templateUrl: "./ticket-table.component.html",
  styleUrl: "./ticket-table.component.scss",
})
export class TicketTableComponent {
  private readonly dialog = inject(MatDialog);
  private readonly transitionDialog = inject(BatchTransitionDialogFacade);

  @Input() ticketBatches: TicketBatch[] = [];
  @Input() management = false;
  @Input() transitionDisabledReason = "";
  get transitionPending(): boolean {
    return this.transitionDialog.pending;
  }
  /**
   * Modo "ingresso único" (evento gratuito): sem lotes, apenas 1 tipo de
   * ingresso. Esconde os botões de lote e trava a criação após o primeiro.
   */
  @Input() singleTicketMode = false;
  @Input() handleEditTicket!: (batchIndex: number, ticketIndex: number) => void;

  @Input() openModalTickets!: (
    batchIndex?: number,
    ticketIndex?: number,
    editBatch?: boolean,
  ) => void;

  @Output() ticketBatchesChange = new EventEmitter<TicketBatch[]>();
  @Output() batchesTransitioned = new EventEmitter<TicketBatch[]>();

  @Output() createBatchClick = new EventEmitter();
  @Output() editBatchClick = new EventEmitter<TicketBatch>();

  @Output() createTicketClick = new EventEmitter<number>();
  /** Criar o ingresso único (gratuito), sem lote explícito. */
  @Output() createSingleTicketClick = new EventEmitter<void>();
  /** Editar as datas de disponibilização do ingresso gratuito. */
  @Output() editAvailabilityClick = new EventEmitter<void>();
  @Output() editTicketClick = new EventEmitter<{
    batchIndex: number;
    ticketIndex: number;
  }>();

  get hasAnyTicket(): boolean {
    return this.ticketBatches.some(
      (batch) => Array.isArray(batch.tickets) && batch.tickets.length > 0,
    );
  }

  canDeleteBatch(batch: TicketBatch): boolean {
    return this.canEditBatch(batch) && canDeletePersistedBatch(batch);
  }

  canEditBatch(batch: TicketBatch): boolean {
    return !batch.transition_closed_at;
  }

  canDeleteTicket(ticket: TicketInBatch, batch?: TicketBatch): boolean {
    return (
      (!batch || this.canEditBatch(batch)) && canDeletePersistedTicket(ticket)
    );
  }

  requestTransition(batch: TicketBatch): void {
    if (
      !this.management ||
      this.singleTicketMode ||
      !batch.id ||
      !this.canEditBatch(batch) ||
      !batch.can_transition ||
      batch.next_batch_number == null ||
      this.transitionDisabledReason ||
      this.transitionPending
    )
      return;
    this.transitionDialog
      .open({
        batchId: batch.id,
        nextBatchNumber: batch.next_batch_number,
      })
      .subscribe((batches: TicketBatch[] | undefined) => {
        if (!Array.isArray(batches) || !batches.length) return;
        this.batchesTransitioned.emit(batches);
      });
  }

  ticketTransition(batch: TicketBatch, ticket: TicketInBatch): TicketTransitionOption | undefined {
    if (!ticket.id || !Array.isArray(batch.ticket_transitions)) return undefined;
    return batch.ticket_transitions.find((item) => item.ticket_id === ticket.id);
  }

  canTransitionTicket(batch: TicketBatch, ticket: TicketInBatch): boolean {
    if (
      !this.management ||
      this.singleTicketMode ||
      !batch.id ||
      !this.canEditBatch(batch) ||
      !!ticket.transition_closed_at ||
      !!this.transitionDisabledReason ||
      this.transitionPending
    ) {
      return false;
    }
    const option = this.ticketTransition(batch, ticket);
    return !!option?.can_transition && option.next_batch_number != null;
  }

  requestTicketTransition(batch: TicketBatch, ticket: TicketInBatch): void {
    const option = this.ticketTransition(batch, ticket);
    if (!batch.id || !ticket.id || !option || !this.canTransitionTicket(batch, ticket)) {
      return;
    }
    this.transitionDialog
      .open({
        batchId: batch.id,
        nextBatchNumber: option.next_batch_number!,
        ticketIds: [ticket.id],
        ticketTitle: this.getTicketDisplayTitle(ticket),
      })
      .subscribe((batches: TicketBatch[] | undefined) => {
        if (!Array.isArray(batches) || !batches.length) return;
        this.batchesTransitioned.emit(batches);
      });
  }

  deleteTicket(batchIndex: number, ticketIndex: number) {
    const ticket = this.ticketBatches[batchIndex]?.tickets?.[ticketIndex];
    const batch = this.ticketBatches[batchIndex];
    if (!ticket || !batch || !this.canDeleteTicket(ticket, batch)) return;

    this.confirmRemoval(
      ticket.id
        ? "Excluir este ingresso? A exclusão será confirmada ao salvar o evento."
        : "Excluir este ingresso?",
    ).subscribe((confirmed) => {
      if (!confirmed || !this.canEditBatch(batch)) return;
      const tickets = this.ticketBatches[batchIndex].tickets;
      tickets.splice(ticketIndex, 1);
      if (!ticket.half_price) {
        this.ticketBatches[batchIndex].tickets = tickets.filter(
          (item) =>
            item.parent_ticket_id !== ticket.id &&
            item.title !== `${ticket.title} — Meia-entrada`,
        );
      }
      this.removeEmptyBatches();
      this.ticketBatchesChange.emit([...this.ticketBatches]);
    });
  }

  deleteBatch(batchIndex: number) {
    const batch = this.ticketBatches[batchIndex];
    if (!batch || !this.canDeleteBatch(batch)) return;

    this.confirmRemoval(
      batch.id
        ? "Excluir este lote? A exclusão será confirmada ao salvar o evento."
        : "Excluir este lote?",
    ).subscribe((confirmed) => {
      if (!confirmed || !this.canEditBatch(batch)) return;
      this.ticketBatches.splice(batchIndex, 1);
      this.removeEmptyBatches();
      this.ticketBatchesChange.emit([...this.ticketBatches]);
    });
  }

  newBatch() {
    this.createBatchClick.emit();
  }

  editBatch(selectedBatch: TicketBatch) {
    if (!this.canEditBatch(selectedBatch)) return;
    this.editBatchClick.emit(selectedBatch);
  }

  getFinalPriceString(price: number, absorbsPlatformFee = false) {
    const taxRate = 0.1;
    const ticketPrice = Number(price);
    const taxValue = ticketPrice * taxRate;

    return absorbsPlatformFee ? ticketPrice : ticketPrice + taxValue;
  }

  getTicketDisplayTitle(ticket: TicketInBatch): string {
    // The category badge already identifies half-price tickets; keep stored names intact.
    const suffix = " — Meia-entrada";
    return ticket.half_price && ticket.title.endsWith(suffix)
      ? ticket.title.slice(0, -suffix.length)
      : ticket.title;
  }

  newTicket(batchIndex: number) {
    const batch = this.ticketBatches[batchIndex];
    if (!batch || !this.canEditBatch(batch)) return;
    this.createTicketClick.emit(batchIndex);
  }

  editTicket(batchIndex: number, ticketIndex: number) {
    const batch = this.ticketBatches[batchIndex];
    if (!batch || !this.canEditBatch(batch)) return;
    this.editTicketClick.emit({
      batchIndex,
      ticketIndex,
    });
  }

  private confirmRemoval(modalContent: string) {
    return this.dialog
      .open(ConfirmActionModalComponent, {
        width: "500px",
        data: {
          modalTitle: "Excluir",
          modalContent,
          confirmMessage: "Excluir",
          cancelMessage: "Cancelar",
        },
      })
      .afterClosed()
      .pipe(take(1));
  }

  private removeEmptyBatches() {
    this.ticketBatches = this.ticketBatches.filter(
      (item) => item.tickets.length > 0,
    );
  }

  showValueWithTax(value: number) {
    const finalPrice = Number(value) + Number(value) * 0.1;
    return finalPrice;
  }

  showVAvailability(value: string) {
    switch (value) {
      case "general public":
        return "Público geral";
      default:
        return value;
    }
  }
}
